import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, Check, ExternalLink, Loader2, Sparkles, Info, X, Gift, Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';
import SEOHead, { generateBreadcrumbSchema } from '@/components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dedicated `/featured` index — ONLY the affiliate storefronts.
 *
 * The previous behaviour landed visitors on `/partners` which is a mixed
 * recruitment + directory page; clicking "Featured Tools" felt like a
 * bait-and-switch ("I want to see Marblism, why are you asking me to
 * become a partner?"). This page solves that by showing the curated
 * 3rd-party AI tools alone, in brand colours, with no "become a partner"
 * messaging anywhere.
 *
 * Structured data is included so Google indexes this as a curated AI
 * tools collection — same SEO play as `/featured/:slug` storefronts.
 */
export default function FeaturedDirectory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter state — synced to URL ?category= so it survives reload + share
  const activeCategory = searchParams.get('category') || 'all';
  // Free-text search — also URL-synced so /featured?q=meeting is shareable.
  const activeQuery = searchParams.get('q') || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Load both affiliate partners (curated tools, free placement)
        // AND platform partners (paid sponsorship lane). They render in
        // the same grid but the platform partners get a "Sponsored"
        // pill so the FTC-disclosure surface is honest.
        const [aff, plat] = await Promise.all([
          axios.get(`${API_URL}/api/affiliate-partners?limit=500`),
          axios.get(`${API_URL}/api/platform-partners`).catch(() => ({ data: { items: [] } })),
        ]);
        const affiliates = (aff.data.partners || []).map((p) => ({
          ...p,
          partner_type: 'affiliate',
        }));
        // Sponsored partners surface FIRST — they're paying for the
        // placement, and they're typically broader-appeal "all-in-one
        // platforms" so a buyer landing here cold gets shown the most
        // versatile tools first.
        const sponsored = (plat.data?.items || []).map((p) => ({
          slug: p.slug,
          name: p.name,
          tagline: p.tagline,
          description: p.description,
          logo_url: p.logo_url,
          category: p.category || 'AI All-in-One',
          website_display: p.website_url
            ? p.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')
            : null,
          partner_type: 'platform',
          // No discount badge for platform partners — they're sponsored,
          // not curated affiliate-deal tools.
          has_private_discount: false,
        }));
        if (!cancelled) setPartners([...sponsored, ...affiliates]);
      } catch (err) {
        console.error('Featured load failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Category list w/ counts — derived from the loaded partners so it stays
  // in sync as new partners are added without a code change.
  const categories = useMemo(() => {
    const counts = new Map();
    partners.forEach((p) => {
      const c = (p.category || 'Other').trim();
      counts.set(c, (counts.get(c) || 0) + 1);
    });
    const arr = Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count); // most-populated first
    return [{ name: 'All', count: partners.length, isAll: true }, ...arr];
  }, [partners]);

  const filteredPartners = useMemo(() => {
    let list = partners;
    if (activeCategory !== 'all') {
      const target = activeCategory.toLowerCase();
      list = list.filter((p) => (p.category || '').toLowerCase() === target);
    }
    const q = activeQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        // Search across the fields a buyer would scan: name, tagline,
        // description, category. Substring match is intentional —
        // partial matches like "meet" → Granola/Stilla matter for
        // discovery and the dataset is small enough that it's instant.
        const hay = [
          p.name,
          p.tagline,
          p.description,
          p.category,
          p.website_display,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [partners, activeCategory, activeQuery]);

  const setCategory = (name) => {
    const next = new URLSearchParams(searchParams);
    if (!name || name.toLowerCase() === 'all') {
      next.delete('category');
    } else {
      next.set('category', name);
    }
    setSearchParams(next, { replace: true });
  };

  const setQuery = (q) => {
    const next = new URLSearchParams(searchParams);
    if (!q) {
      next.delete('q');
    } else {
      next.set('q', q);
    }
    setSearchParams(next, { replace: true });
  };

  // Category-aware SEO. Each `/featured?category=X` becomes a discoverable
  // landing page with its own title, description, canonical, OG card, and
  // ItemList schema scoped to the filtered subset. Empty categories fall
  // back to the all-tools meta automatically.
  const seo = useMemo(() => {
    const isFiltered = activeCategory !== 'all';
    const category = isFiltered ? activeCategory : '';
    const itemsForSchema = isFiltered ? filteredPartners : partners;

    // Pull a few partner names for the description so the meta line gives
    // search engines (and humans on a SERP) something tangible to chew on.
    const exemplars = itemsForSchema.slice(0, 3).map((p) => p.name).filter(Boolean);
    const exemplarPhrase = exemplars.length
      ? exemplars.join(', ') + (itemsForSchema.length > 3 ? ', and more' : '')
      : 'curated AI tools';

    let title;
    let description;
    let path;
    let keywords;

    if (isFiltered) {
      title = `${category} — Featured AI Tools`;
      description = (
        `Hand-picked ${category} tools available on theailedger.ai. ` +
        `${exemplarPhrase}. Discover the AI partners we recommend to ` +
        `creators and businesses.`
      ).slice(0, 300);
      // Use a slug-friendly path so canonical stays clean — Google treats
      // this as a unique URL while the underlying app still uses ?category=
      const catSlug = category
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      path = `/featured?category=${encodeURIComponent(category)}`;
      keywords = (
        `${category}, ${category} tools, ${category} AI tools, best ${category} tools 2026, ` +
        `featured AI tools, AI marketplace, theailedger ${catSlug}`
      );
    } else {
      title = 'Featured AI Tools — Hand-picked partners';
      description = (
        `Hand-picked AI tools available to theailedger.ai customers, with ` +
        `exclusive perks. ${exemplarPhrase}.`
      ).slice(0, 300);
      path = '/featured';
      keywords = (
        'featured AI tools, AI marketplace, AI partner directory, ' +
        'best AI tools 2026, theailedger.ai featured'
      );
    }

    const collectionSchema = {
      '@type': 'CollectionPage',
      name: title,
      url: `https://theailedger.ai${path}`,
      description,
      ...(isFiltered && { about: category }),
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: itemsForSchema.length,
        itemListElement: itemsForSchema.map((p, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          item: {
            '@type': 'Organization',
            name: p.name,
            url: `https://theailedger.ai/featured/${p.slug}`,
            description: p.tagline || (p.description || '').slice(0, 160),
            ...(p.logo_url
              ? { logo: p.logo_url.startsWith('http') ? p.logo_url : `https://theailedger.ai${p.logo_url}` }
              : {}),
          },
        })),
      },
    };

    const breadcrumbs = isFiltered
      ? [
          { name: 'Home', path: '/' },
          { name: 'Featured AI Tools', path: '/featured' },
          { name: category, path },
        ]
      : [
          { name: 'Home', path: '/' },
          { name: 'Featured AI Tools', path: '/featured' },
        ];

    // Wrap in @graph so we ship both CollectionPage + BreadcrumbList in a
    // single JSON-LD block (AI Overviews + Google rich results both
    // require Breadcrumb to render the result chain).
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [collectionSchema, generateBreadcrumbSchema(breadcrumbs)],
    };

    return { title, description, path, keywords, schema };
  }, [activeCategory, partners, filteredPartners]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEOHead
        title={seo.title}
        description={seo.description}
        path={seo.path}
        keywords={seo.keywords}
        schema={seo.schema}
      />

      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-40">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
            data-testid="featured-home-btn"
          >
            <Logo />
          </button>
          <Button
            onClick={() => navigate('/submit')}
            className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold"
            data-testid="featured-sell-cta"
          >
            Sell your AI tool →
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,240,255,0.15),transparent_60%)]" />
        <div className="relative container mx-auto px-6 md:px-12 py-14 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 mb-6">
            <Sparkles className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-sm text-[#00F0FF] font-semibold tracking-wide">
              FEATURED AI TOOLS
            </span>
          </div>
          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-black mb-5 leading-tight text-white"
            data-testid="featured-h1"
          >
            {activeCategory === 'all' ? (
              <>
                Hand-picked AI tools<br />
                <span className="text-[#00F0FF]">
                  you can use today
                </span>
              </>
            ) : (
              <>
                <span className="text-[#00F0FF]">{activeCategory}</span><br />
                tools we&rsquo;d use ourselves
              </>
            )}
          </h1>
          {!loading && partners.length > 0 && (
            <p className="text-sm text-neutral-500" data-testid="featured-count">
              {activeCategory === 'all' ? (
                <>
                  {partners.length} {partners.length === 1 ? 'tool' : 'tools'} available now &middot; more added weekly
                </>
              ) : (
                <>
                  Showing {filteredPartners.length} {filteredPartners.length === 1 ? 'tool' : 'tools'} in <span className="text-[#00F0FF]">{activeCategory}</span> &middot; {partners.length} total
                </>
              )}
            </p>
          )}
        </div>
      </section>

      {/* Directory */}
      <main className="container mx-auto px-6 md:px-12 py-12 md:py-16">
        {/* Search bar — debounceless, instant client-side filter against
            name/tagline/description/category. URL-synced via ?q= so a
            shared link reproduces the same view. */}
        {!loading && partners.length > 4 && (
          <div className="mb-5 max-w-xl" data-testid="featured-search-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
              <input
                type="search"
                value={activeQuery}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${partners.length} tools — try "meeting", "video", "voice"…`}
                aria-label="Search featured tools"
                data-testid="featured-search-input"
                className="w-full bg-black/40 border border-white/15 rounded-xl pl-10 pr-9 py-3 text-sm text-white placeholder:text-neutral-500 outline-none focus:border-[#00F0FF]/60 focus:bg-black/60 transition-colors"
              />
              {activeQuery && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  data-testid="featured-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {activeQuery && (
              <p className="mt-2 text-xs text-neutral-500" data-testid="featured-search-results-count">
                {filteredPartners.length === 0
                  ? <>No matches for <span className="text-white">&ldquo;{activeQuery}&rdquo;</span></>
                  : <>{filteredPartners.length} {filteredPartners.length === 1 ? 'match' : 'matches'} for <span className="text-white">&ldquo;{activeQuery}&rdquo;</span></>
                }
              </p>
            )}
          </div>
        )}

        {/* Category filter pills — appear once data has loaded and we
            have at least 2 categories worth filtering by. Synced to the
            URL so /featured?category=AI%20Productivity is shareable. */}
        {!loading && categories.length > 2 && (
          <div
            className="mb-8 flex flex-wrap items-center gap-2"
            data-testid="featured-categories"
          >
            <span className="text-xs uppercase tracking-wider text-neutral-500 mr-1">
              Filter:
            </span>
            {categories.map((c) => {
              const slug = c.isAll ? 'all' : c.name;
              const isActive =
                (activeCategory === 'all' && c.isAll) ||
                (!c.isAll && activeCategory === c.name);
              return (
                <button
                  key={c.name}
                  data-testid={`category-pill-${(c.isAll ? 'all' : c.name).toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setCategory(slug)}
                  className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
                    isActive
                      ? 'border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]'
                      : 'border-white/10 text-neutral-400 hover:border-white/30 hover:text-white'
                  }`}
                >
                  {c.name}
                  <span className={`ml-1.5 ${isActive ? 'text-[#00F0FF]/70' : 'text-neutral-600'}`}>
                    {c.count}
                  </span>
                </button>
              );
            })}
            {activeCategory !== 'all' && (
              <button
                data-testid="category-clear"
                onClick={() => setCategory('all')}
                className="ml-1 inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-white"
              >
                <X className="w-3 h-3" /> clear
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-10 h-10 text-[#00F0FF] animate-spin" />
          </div>
        ) : filteredPartners.length === 0 ? (
          <div className="text-center py-20" data-testid="featured-empty">
            <Sparkles className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">
              {partners.length === 0
                ? 'More tools coming soon'
                : activeQuery
                  ? <>No matches for <span className="text-[#00F0FF]">&ldquo;{activeQuery}&rdquo;</span></>
                  : `No tools in ${activeCategory} yet`}
            </h2>
            <p className="text-neutral-400">
              {partners.length === 0
                ? 'We\u2019re adding new featured AI tools every week. Check back shortly.'
                : activeQuery
                  ? 'Try a different keyword or clear the search to see all tools.'
                  : 'Try a different category or view all tools.'}
            </p>
            {partners.length > 0 && (
              <Button
                variant="outline"
                onClick={() => { setCategory('all'); setQuery(''); }}
                className="mt-6 border-white/20 bg-transparent text-white hover:bg-white/5"
                data-testid="featured-empty-show-all"
              >
                Show all {partners.length} tools
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="featured-grid">
            {filteredPartners.map((p) => (
              <FeaturedCard key={p.slug} partner={p} navigate={navigate} />
            ))}
          </div>
        )}

        {/* FTC disclosure — global, conspicuous, near the cards */}
        <p
          className="mt-10 text-center text-[11px] text-neutral-600 max-w-2xl mx-auto"
          data-testid="featured-disclosure"
        >
          theailedger.ai may earn a commission when you sign up for a featured tool through
          our links, at no extra cost to you. Tools marked &ldquo;Sponsored&rdquo; pay a flat
          monthly fee for placement; we still only feature tools we&rsquo;d use ourselves.
        </p>
      </main>
    </div>
  );
}

function FeaturedCard({ partner, navigate }) {
  const isPlatform = partner.partner_type === 'platform';

  const handleClick = async () => {
    if (isPlatform) {
      // Track click then redirect off-site directly. Platform partners
      // pay for placement so the goal is direct conversion, not a
      // detail page detour.
      try {
        const res = await axios.post(
          `${API_URL}/api/platform-partners/${partner.slug}/click`
        );
        const url = res.data?.redirect_url;
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
          return;
        }
      } catch (err) {
        console.error('Platform click failed:', err);
      }
      // Fallback if click endpoint is down — still take the user
      // somewhere useful.
      window.open(`https://${partner.website_display || ''}`, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(`/featured/${partner.slug}`);
  };

  return (
    <button
      onClick={handleClick}
      className={`group relative text-left bg-neutral-900/50 border rounded-2xl p-6 transition-all ${
        isPlatform
          ? 'border-[#FFB400]/30 hover:border-[#FFB400]/60'
          : 'border-white/10 hover:border-[#00F0FF]/50'
      }`}
      data-testid={`featured-card-${partner.slug}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-14 h-14 rounded-xl bg-black/60 border border-white/5 flex items-center justify-center overflow-hidden p-2">
          {partner.logo_url ? (
            <img
              src={partner.logo_url}
              alt={partner.name}
              className="w-full h-full object-contain"
              loading="lazy"
            />
          ) : (
            <Sparkles className="w-6 h-6 text-[#00F0FF]" />
          )}
        </div>
        {isPlatform ? (
          <span
            data-testid={`sponsored-pill-${partner.slug}`}
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#FFB400] bg-[#FFB400]/10 border border-[#FFB400]/30 rounded-full px-2 py-0.5"
            title="Sponsored placement — see disclosure below"
          >
            <Sparkles className="w-3 h-3" /> Sponsored
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#00F0FF]">
            <Sparkles className="w-3 h-3" /> Featured
          </span>
        )}
      </div>

      {partner.has_private_discount && (
        <div
          data-testid={`discount-badge-${partner.slug}`}
          className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFB400]/15 border border-[#FFB400]/40 text-[#FFB400] text-[10px] font-bold uppercase tracking-wider"
          title={partner.private_discount_description || 'Exclusive discount'}
        >
          <Gift className="w-2.5 h-2.5" /> Discount
        </div>
      )}

      <h3 className={`text-xl font-bold mb-1 transition-colors ${
        isPlatform ? 'group-hover:text-[#FFB400]' : 'group-hover:text-[#00F0FF]'
      }`}>
        {partner.name}
      </h3>
      {partner.tagline && (
        <p className="text-xs uppercase tracking-wider text-neutral-500 mb-3">
          {partner.tagline}
        </p>
      )}
      {partner.description && (
        <p className="text-sm text-neutral-400 line-clamp-3 min-h-[3.6em]">
          {partner.description}
        </p>
      )}

      {partner.customer_benefit && (
        <div className="mt-4 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/30 text-[#00FF94] font-semibold">
          <Check className="w-3 h-3" /> {partner.customer_benefit}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
        <span className="text-xs text-neutral-500">{partner.category}</span>
        <span className={`text-xs inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform ${
          isPlatform ? 'text-[#FFB400]' : 'text-[#00F0FF]'
        }`}>
          {isPlatform ? <>Visit {partner.name} <ExternalLink className="w-3 h-3" /></> : <>View details <ArrowRight className="w-3 h-3" /></>}
        </span>
      </div>

      {partner.website_display && (
        <span className="absolute bottom-3 left-6 text-[10px] text-neutral-700 inline-flex items-center gap-1">
          <ExternalLink className="w-2.5 h-2.5" />
          {partner.website_display}
        </span>
      )}
    </button>
  );
}
